import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSTitleCard } from '@/components/shared/card/Card';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dayIndexes } from '@/helpers/dayIndexes';
import { useEmployeeWorkingPatternQuery, WorkingPatternDay } from '@/services/gql/graphql.generated';
import strings from '../ess.translations';

import { secondsToTimeString } from '@/helpers/dateHelper';
import { FunctionComponent, Suspense } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { MyScheduleHeader } from './MyScheduleHeader';

const MySchedule: FunctionComponent = () => {
  const navigate = useNavigate();

  const [{ data: myWorkingPatternData }] = useEmployeeWorkingPatternQuery({
    requestPolicy: 'network-only'
  });

  if (
    !myWorkingPatternData?.myWorkingPatterns?.periods ||
    myWorkingPatternData?.myWorkingPatterns?.periods.length === 0
  ) {
    throw Error('Employee working pattern data should be setup');
  }

  let patterns = myWorkingPatternData?.myWorkingPatterns?.periods[0].patterns as WorkingPatternDay[];
  if (!patterns) {
    throw Error('Patterns should be defined.');
  }

  const onEditClick = () => {
    navigate(`edit`);
  };

  const groupedSchedule = patterns.reduce<Record<number, WorkingPatternDay[]>>((acc, item) => {
    const key = item.dayNumber ?? -1;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    acc[key].sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));
    return acc;
  }, {});

  const orderedSchedule = dayIndexes.map(dayNumber => {
    const periods = (groupedSchedule[dayNumber] ?? []).filter(
      period => period.startTime != null && period.endTime != null && !(period.startTime === 0 && period.endTime === 0)
    );
    return {
      dayNumber,
      day: groupedSchedule[dayNumber]?.[0]?.day ?? strings.mySchedule.dayLabels[dayNumber],
      periods
    };
  });

  return (
    <PageContainer customHeader={<MyScheduleHeader />}>
      <Suspense fallback={<TableLoader />}>
        <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
          <TSTitleCard
            primaryAction={{
              label: strings.mySchedule.editLabel,
              onClick: onEditClick
            }}
            title={strings.mySchedule.cardTitle}
            icon={AiOutlineCalendar}
          >
            <div className="grid grid-flow-row grid-cols-1 sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-4 gap-y-5 gap-x-8 lg:max-w-6xl lg:mx-auto lg:px-8">
              {orderedSchedule.map(item => (
                <div
                  key={item.dayNumber}
                  className="grid grid-cols-2 gap-x-6 items-start"
                  style={{ gridTemplateColumns: '0.8fr 1.2fr' }}
                >
                  <p className="text-right font-semibold sm:px-0">{item.day}</p>
                  <p className="break-keep">
                    {item.periods.length > 0
                      ? item.periods
                          .map(
                            period =>
                              `${secondsToTimeString(period.startTime!)} - ${secondsToTimeString(period.endTime!)}`
                          )
                          .join(', ')
                      : strings.mySchedule.notAvailable}
                  </p>
                </div>
              ))}
            </div>
          </TSTitleCard>
        </PageSizer>
      </Suspense>
    </PageContainer>
  );
};

export default MySchedule;
